import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectMarkets,
  selectPathFinderRegions,
  selectTrades,
} from '../scenarios/scenarioSlice';
import {
  selectFilteredEdgeNodes,
  selectFilteredEdgePoints,
} from '../map/slice/mapSelectors';
import { fromLonLat } from 'ol/proj';
import { selectPlotterMarkets } from '../plotter/plotterSelectors';
import { colors, interpolateColor } from '../utils/colors';
import { isValidNumber } from '../utils/numberUtils';
import { Market } from '@/types';
import { useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';

const getMarketStyle = () => {
  return {
    'circle-radius': 10,
    'circle-fill-color': ['get', 'fillColor'],
    'circle-stroke-color': ['get', 'color'],
    'circle-stroke-width': 2,
    'circle-rotate-with-view': false,
    'circle-opacity': 0.8,
  };
};

export type MapMarketsProps = {
  zIndex: number;
  markets?: Market[];
};

export const MapMarkets: React.FC<MapMarketsProps> = ({
  zIndex = 0,
  ...props
}) => {
  const scenarioMarkets = useSelector(selectMarkets);
  const scenarioTrades = useSelector(selectTrades);
  const plotterMarkets = useSelector(selectPlotterMarkets);
  const pathFinderRegions = useSelector(selectPathFinderRegions);

  const points = useSelector(selectFilteredEdgePoints);
  const nodes = useSelector(selectFilteredEdgeNodes);

  const markets = useMemo(
    () => [...scenarioMarkets, ...(props.markets || [])],
    [scenarioMarkets, props.markets],
  );
  const marketsAndTrades = useMemo(
    () =>
      [...markets, ...plotterMarkets, ...scenarioTrades].filter((market) => {
        if (pathFinderRegions.length === 0) return true;
        const point = points[market.point];
        return (
          point &&
          pathFinderRegions.some(
            (region) =>
              region.tsps.some((tsp) => tsp.id === point.tsp_id) ||
              region.zones.some(
                (zone) =>
                  zone.id === point.receipt_zone ||
                  zone.id === point.delivery_zone,
              ),
          )
        );
      }),
    [markets, scenarioTrades, plotterMarkets, pathFinderRegions, points],
  );

  const prices = useMemo(
    () =>
      marketsAndTrades
        .map((market) =>
          market.price ? parseFloat(market.price.toString()) : null,
        )
        .filter(isValidNumber) as number[],
    [marketsAndTrades],
  );
  const maxPrice = useMemo(() => Math.max(...prices), [prices]);
  const minPrice = useMemo(() => Math.min(...prices), [prices]);

  const marketFeatures = useMemo<FeatureLike[]>(
    () =>
      marketsAndTrades
        .map((market) => {
          const nodeId = points[market.point]?.node;
          const node = nodes[nodeId ?? ''];

          if (node) {
            // Prefer explicit colors for PathFinder plotter markets
            const isPfReceipt = market.description === 'PathFinder Receipt';
            const isPfDelivery = market.description === 'PathFinder Delivery';

            let color: string;
            if (isPfReceipt) {
              color = colors.elements.buyMarketColor;
            } else if (isPfDelivery) {
              color = colors.elements.sellMarketColor;
            } else {
              const ratio = market.price
                ? (parseFloat(market.price.toString()) - minPrice) /
                  (maxPrice - minPrice)
                : null;
              color = isValidNumber(ratio)
                ? interpolateColor(
                    colors.elements.minMarketColor,
                    colors.elements.maxMarketColor,
                    ratio as number,
                  ).slice(0, 7)
                : colors.elements.invalidMarketColor;
            }
            const fillColor = `${color}30`;

            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: fromLonLat([Number(node.lng), Number(node.lat)]),
              },
              properties: {
                id: nodeId,
                key: `node-${nodeId}`,
                color,
                fillColor,
              },
            };
          }

          return null;
        })
        .filter(Boolean) as FeatureLike[],
    [marketsAndTrades, nodes, points],
  );

  const style = useMemo(() => getMarketStyle(), []);

  useMapLayer(marketFeatures, { zIndex, style });

  return <></>;
};
